import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useOutletContext, useLocation } from "react-router-dom";
import {
	useForm,
	FormProvider,
	useFieldArray,
	useWatch,
} from "react-hook-form";
import { useGlobalSlice } from "app/slice";
import { selectLines } from "app/slice/selectors";
import { PhoneSelector } from "./phone-selector";
import { SimCardNumber } from "./sim-card-number";
import { VLEMobileSimDialog } from "./vle-mobile-sim-dialog";
import { CongratsDialog } from "./congrats-dialog";
import { toast } from "app/components/ui/use-toast";

import { Button } from "app/components/ui/button";
import { Icons } from "app/components/ui/icons";

type SimType = 'eSim' | 'sim';

interface FlowState {
  showAllLines: boolean;
  physicalSimIndices: number[];
  eSimIndices: number[];
}

interface OutletContext {
	selectedLines: number;
	isSimCardView: boolean;
	setIsSimCardView: React.Dispatch<React.SetStateAction<boolean>>;
	setFlowState: React.Dispatch<React.SetStateAction<FlowState | null>>;
	compatResults: any[];
	setCompatResults: React.Dispatch<React.SetStateAction<any[]>>;
}

interface PhoneEntry {
	brand: string;
	model: string;
	simType: string;
}

interface FormValues {
	phones: PhoneEntry[];
	simNumbers: Record<number, string>;
	imeiNumbers: Record<number, string>;
}

interface VerificationResult {
  success: boolean;
  message: string;
  deviceId?: string;
  imei?: string;
}

interface LocationState {
  from?: string;
  getNewLineData?: any;
  multiNetworkPlanId?: string;
}

export const PhoneType = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const locationState = location.state as LocationState;
	const dispatch = useDispatch();
	const { actions } = useGlobalSlice();
	const {
		selectedLines,
		isSimCardView,
		setIsSimCardView,
		setFlowState,
		compatResults,
		setCompatResults
	} = useOutletContext<OutletContext>();

	const [showVLESimDialog, setShowVLESimDialog] = useState(false);
	const [showCongratsDialog, setShowCongratsDialog] = useState(false);
	const [verificationStatus, setVerificationStatus] = useState<{ [key: number]: { simVerified: boolean; imeiVerified: boolean } }>({});
	const lines = useSelector(selectLines);

	const numberOfLines = lines || selectedLines;

	const methods = useForm<FormValues>({
		defaultValues: {
			phones: Array.from({ length: numberOfLines }).map(() => ({
				brand: "",
				model: "",
				simType: "" as SimType,
			})),
			simNumbers: {},
			imeiNumbers: {},
		},
	});

	const { fields } = useFieldArray({
		control: methods.control,
		name: "phones",
	});

	const phoneEntries = useWatch({
		control: methods.control,
		name: "phones",
		defaultValue: methods.getValues("phones"),
	});

	// Watch form data for SIM and IMEI numbers
	const simNumbers = useWatch({
		control: methods.control,
		name: "simNumbers",
	});

	const imeiNumbers = useWatch({
		control: methods.control,
		name: "imeiNumbers",
	});

	const { allESim, allPhysical, physicalSimIndices, eSimIndices } = useMemo(() => {
		const physicalIndices: number[] = [];
		const eSimIndices: number[] = [];
		
		phoneEntries.forEach((entry, index) => {
			if (entry.simType === "sim") physicalIndices.push(index);
			else if (entry.simType === "eSim") eSimIndices.push(index);
		});

		return {
			allESim: physicalIndices.length === 0,
			allPhysical: eSimIndices.length === 0,
			physicalSimIndices: physicalIndices,
			eSimIndices: eSimIndices
		};
	}, [phoneEntries]);

	console.log("eSimIndices :", eSimIndices, "physicalIndices :", physicalSimIndices);

	const handleDialogConfirm = (hasPhysicalSim: boolean) => {
		if (hasPhysicalSim) {
			//Show SIM card numbers for physical SIMs
			setIsSimCardView(true);
		} else {
			// Navigate to number type with all lines
			setFlowState({
				showAllLines: true,
				physicalSimIndices: [],
				eSimIndices: Array.from({ length: numberOfLines }, (_, i) => i)
			});
			console.log("=== PHONE TYPE DIALOG CONFIRM NAVIGATION DEBUG ===");
			console.log("Navigating to /multi-network-number-type with state:", locationState);
			console.log("getNewLineData:", locationState?.getNewLineData);
			console.log("getNewLineData.data:", locationState?.getNewLineData?.data);
			console.log("multiNetworkPlanId:", locationState?.getNewLineData?.data?.multinetworkPlanId);
			console.log("=================================================");
			
			navigate("/multi-network-number-type", {
				state: locationState
			});
		}
	};

	// Check if all physical SIMs have been verified successfully
	const areAllPhysicalSimsVerified = useMemo(() => {
		return physicalSimIndices.every(index => {
			const status = verificationStatus[index];
			return status && status.simVerified && status.imeiVerified;
		});
	}, [physicalSimIndices, verificationStatus]);

	const validatePhoneSelections = (data: FormValues) => {
		return data.phones.every(entry => entry.brand && entry.model && entry.simType);
	};

	// Handle verification status changes from SimCardNumber components
	const handleVerificationChange = (cardIndex: number, simVerified: boolean, imeiVerified: boolean) => {
		setVerificationStatus(prev => ({
			...prev,
			[cardIndex]: { simVerified, imeiVerified }
		}));
	};

	const onSubmit = async (data: FormValues) => {
		try {
			if (!validatePhoneSelections(data)) {
				toast({
					title: "Incomplete Selection",
					description: "Please complete all phone selections before continuing",
					variant: "error",
				});
				return;
			}

			// Update compatibility results one final time before proceeding
			const finalCompatResults = compatResults.map((result, index) => {
				if (!result) return result;
				const simType = data.phones[index].simType;
				return {
					...result,
					simType,
					is_esim: simType === "eSim" ? "Y" : "N",
				};
			});
			
			setCompatResults(finalCompatResults);
			dispatch(actions.setCompatibilitiesData({ CDS: finalCompatResults }));

			if (isSimCardView) {
				// Check if all physical SIMs have been verified using verification status
				const allPhysicalSimsVerified = physicalSimIndices.every(index => {
					// const simNumber = data.simNumbers[index];
          // const imeiNumber = data.imeiNumbers[index];
          // return simNumber && imeiNumber && 
          //      simNumber.replace(/\s/g, '').length >= 19 && 
          //      imeiNumber.replace(/\s/g, '').length === 15;
					const status = verificationStatus[index];
					return status && status.simVerified && status.imeiVerified;
				});

				if (!allPhysicalSimsVerified) {
					toast({
						title: "Incomplete Verification",
						description: "Please verify all SIM and IMEI numbers before continuing",
						variant: "error",
					});
					return;
				}

				console.log("All verifications successful:", {
					simNumbers: data.simNumbers,
					imeiNumbers: data.imeiNumbers,
					physicalSimIndices
				});
				
				setFlowState({
					showAllLines: false,
					physicalSimIndices: [],
					eSimIndices
				});
				
							if (eSimIndices.length === 0) {
				// Navigate directly to number-type page when all are physical SIMs
				navigate("/multi-network-number-type", {
					state: locationState
				});
			} else {
				console.log("=== PHONE TYPE NAVIGATION DEBUG ===");
				console.log("Navigating to /multi-network-number-type with state:", locationState);
				console.log("getNewLineData:", locationState?.getNewLineData);
				console.log("getNewLineData.data:", locationState?.getNewLineData?.data);
				console.log("multiNetworkPlanId:", locationState?.getNewLineData?.data?.multinetworkPlanId);
				console.log("=====================================");
				
				navigate("/multi-network-number-type", {
					state: locationState
				});
			}
				return;
			}

			if (allESim) {
				setFlowState({
					showAllLines: false,
					physicalSimIndices: [],
					eSimIndices: Array.from({ length: numberOfLines }, (_, i) => i)
				});
				console.log("=== PHONE TYPE ALL ESIM NAVIGATION DEBUG ===");
				console.log("Navigating to /multi-network-number-type with state:", locationState);
				console.log("getNewLineData:", locationState?.getNewLineData);
				console.log("getNewLineData.data:", locationState?.getNewLineData?.data);
				console.log("multiNetworkPlanId:", locationState?.getNewLineData?.data?.multinetworkPlanId);
				console.log("=============================================");
				
				navigate("/multi-network-number-type", {
					state: locationState
				});
				return;
			}

			setShowVLESimDialog(true);
		} catch (error) {
			console.error("Submission error:", error);
			toast({
				title: "Submission Error",
				description: `An unexpected error occurred: ${error instanceof Error ? error.message : String(error)}`,
				variant: "error",
			});
		}
	};

	return (
		<React.Fragment>
			<FormProvider {...methods}>
				<form
					className="w-full max-w-[31rem] md:mx-auto space-y-4 sm:space-y-5 lg:space-y-6"
					onSubmit={methods.handleSubmit(onSubmit)}
				>
					{isSimCardView ? (
						<div className="space-y-5 sm:space-y-6 lg:space-y-[1.875rem]">
							{physicalSimIndices.map((lineIndex) => (
								<SimCardNumber
									key={lineIndex}
									lineNumber={fields.length !== 1 ? lineIndex + 1 : 0}
									cardIndex={lineIndex}
									onVerificationChange={(simVerified, imeiVerified) => 
										handleVerificationChange(lineIndex, simVerified, imeiVerified)
									}
								/>
							))}
							<Button type="submit" variant="primary">
								Continue
							</Button>
						</div>
					) : (
						<>
							{fields.map((_, index) => (
								<PhoneSelector
									key={index}
									index={index}
									lineNumber={fields.length !== 1 ? index + 1 : 0}
									numberOfLines={numberOfLines}
								/>
							))}
							<Button type="submit" variant="primary">
								Continue
							</Button>
						</>
					)}
				</form>
			</FormProvider>
			<VLEMobileSimDialog
				showDialog={showVLESimDialog}
				setShowDialog={setShowVLESimDialog}
				onConfirm={handleDialogConfirm}
				// isMixedCase={!allESim && !allPhysical}
			/>
			<CongratsDialog
				showDialog={showCongratsDialog}
				setShowDialog={setShowCongratsDialog}
				locationState={locationState}
			/>
		</React.Fragment>
	);
};
